export type RuleAction = "manage" | "create" | "read" | "update" | "delete";
export type RuleSubject =
  | "User"
  | "Account"
  | "Connection"
  | "EltSync"
  | "ReverseEtlSync"
  | "TransformLayer"
  | "QueryExecution"
  | "Orchestration"
  | "ApiKey"
  | "StripeSubscription"
  | "AuditLog"
  | "Notification"
  | "AccessGroup"
  | "all";

export type Permission = `${RuleAction}:${RuleSubject}`;

/**
 * Maps semantic UI actions to the backend permission(s) they require.
 *
 * This is the single place to update when authorization policy changes.
 * UI components reference AppAction keys — raw Permission strings stay here.
 *
 * A user passes a check if they hold ANY of the listed permissions,
 * or if they have the admin wildcard `manage:all`.
 */
export const PERMISSION_MAP = {
  // Connections
  createConnection: ["create:Connection"],
  updateConnection: ["update:Connection"],
  deleteConnection: ["delete:Connection"],

  // ELT Syncs
  createEltSync: ["create:EltSync"],
  updateEltSync: ["update:EltSync"],
  deleteEltSync: ["delete:EltSync"],

  // Notifications / Alerts (dismiss, resolve, retry)
  resolveNotification: ["delete:Notification"],

  // Reverse ETL Syncs
  createReverseEtlSync: ["create:ReverseEtlSync"],
  updateReverseEtlSync: ["update:ReverseEtlSync"],
  deleteReverseEtlSync: ["delete:ReverseEtlSync"],

  // Transform / Models
  createModel: ["create:TransformLayer"],
  updateModel: ["update:TransformLayer"],
  deleteModel: ["delete:TransformLayer"],

  // Orchestration
  createOrchestration: ["create:Orchestration"],
  updateOrchestration: ["update:Orchestration"],
  deleteOrchestration: ["delete:Orchestration"],

  // Query execution (viewer can run read-only queries)
  runQuery: ["create:QueryExecution"],
  runModelTests: ["create:QueryExecution"],

  // Data Warehouse configuration (admin-only — manage:Account not granted to non-admins)
  updateDataWarehouse: ["manage:Account"],
  // Transform settings (admin-only)
  updateTransformSettings: ["manage:Account"],
  // AI data sharing (admin-only)
  updateAccountAiSettings: ["manage:Account"],

  // Workspace management (admin-only)
  updateAccountSettings: ["update:Account"],
  deleteAccount: ["delete:Account"],
  manageBilling: ["manage:StripeSubscription"],
  viewAuditLogs: ["read:AuditLog"],
  inviteMembers: ["create:User"],
  removeMembers: ["delete:User"],
  changeRoles: ["manage:User"],

  // API Keys (editor+)
  manageApiKeys: ["manage:ApiKey"],

  // Data access policy (admin-only — manage:AccessGroup is only reachable via manage:all)
  manageAccessGroups: ["manage:AccessGroup"],
} as const satisfies Record<string, readonly Permission[]>;

export type AppAction = keyof typeof PERMISSION_MAP;
